import { useQuery } from '@tanstack/react-query'
import { consoleQueries, getApiErrorMessage } from '@milliswift/api-client'
import {
  Badge,
  Callout,
  Card,
  CardBody,
  DataTable,
  PageSkeleton,
  PreviewDataBadge,
  SectionHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  type Column,
} from '@milliswift/ui'
import { Link, useParams } from 'react-router-dom'
import type { TableColumn, TableIndex, TableStatistics } from '@milliswift/types'
import { useDb } from '../lib/db'

/**
 * One table: columns, indexes, statistics. "Statistics" reports per-column
 * facts — distinct count, nulls, min/max — and nothing about hardware.
 */
export default function TableDetail() {
  const { name = '' } = useParams()
  const { databaseId } = useDb()

  const detail = useQuery({
    ...consoleQueries.table(databaseId ?? '', name),
    enabled: Boolean(databaseId) && Boolean(name),
  })

  if (detail.isPending) return <PageSkeleton />

  if (detail.isError) {
    return (
      <Callout tone="danger" title="Could not load this table">
        {getApiErrorMessage(detail.error)}{' '}
        <Link to="/tables" className="font-medium underline underline-offset-2">
          Back to tables
        </Link>
      </Callout>
    )
  }

  const { table, columns, indexes, statistics } = detail.data

  const columnCols: Column<TableColumn>[] = [
    {
      key: 'name',
      header: 'Column',
      sortValue: (c) => c.name,
      cell: (c) => <span className="font-mono font-medium text-foreground">{c.name}</span>,
    },
    {
      key: 'type',
      header: 'Type',
      cell: (c) => <Badge tone="outline">{c.data_type}</Badge>,
    },
    {
      key: 'nullable',
      header: 'Nullable',
      cell: (c) => (c.nullable ? <span className="text-muted">yes</span> : <span className="text-foreground">no</span>),
    },
    {
      key: 'default',
      header: 'Default',
      cell: (c) => <span className="font-mono text-caption text-muted">{c.default_value ?? '—'}</span>,
    },
    {
      key: 'comment',
      header: 'Comment',
      cell: (c) => <span className="text-caption text-muted">{c.comment ?? ''}</span>,
    },
  ]

  const indexCols: Column<TableIndex>[] = [
    {
      key: 'name',
      header: 'Index',
      cell: (i) => <span className="font-mono text-foreground">{i.name}</span>,
    },
    {
      key: 'columns',
      header: 'Columns',
      cell: (i) => <span className="font-mono text-caption text-muted">{i.columns.join(', ')}</span>,
    },
    {
      key: 'unique',
      header: 'Unique',
      cell: (i) => (i.unique ? <Badge tone="info">unique</Badge> : null),
    },
  ]

  const statCols: Column<TableStatistics>[] = [
    {
      key: 'column',
      header: 'Column',
      sortValue: (s) => s.column,
      cell: (s) => <span className="font-mono text-foreground">{s.column}</span>,
    },
    {
      key: 'distinct',
      header: 'Distinct',
      align: 'right',
      sortValue: (s) => s.distinct_count,
      cell: (s) => <span className="font-mono">{s.distinct_count.toLocaleString()}</span>,
    },
    {
      key: 'nulls',
      header: 'Nulls',
      align: 'right',
      sortValue: (s) => s.null_count,
      cell: (s) => <span className="font-mono">{s.null_count.toLocaleString()}</span>,
    },
    {
      key: 'min',
      header: 'Min',
      cell: (s) => <span className="font-mono text-caption text-muted">{s.min ?? '—'}</span>,
    },
    {
      key: 'max',
      header: 'Max',
      cell: (s) => <span className="font-mono text-caption text-muted">{s.max ?? '—'}</span>,
    },
    {
      key: 'avg',
      header: 'Average',
      align: 'right',
      cell: (s) =>
        s.average !== null ? <span className="font-mono">{s.average.toLocaleString()}</span> : <span className="text-subtle">—</span>,
    },
  ]

  return (
    <div className="space-y-6">
      <SectionHeader
        title={table.table_name}
        description={`${table.row_count.toLocaleString()} rows · ${
          table.size_bytes >= 1e9
            ? `${(table.size_bytes / 1e9).toFixed(1)} GB`
            : `${(table.size_bytes / 1e6).toFixed(1)} MB`
        } · cache: ${table.residency}`}
        action={<PreviewDataBadge />}
      />

      <Tabs defaultValue="columns">
        <TabsList>
          <TabsTrigger value="columns">Columns ({columns.length})</TabsTrigger>
          <TabsTrigger value="indexes">Indexes ({indexes.length})</TabsTrigger>
          <TabsTrigger value="statistics">Statistics</TabsTrigger>
        </TabsList>

        <TabsContent value="columns">
          <DataTable data={columns} columns={columnCols} getRowId={(c) => c.name} />
        </TabsContent>

        <TabsContent value="indexes">
          <DataTable data={indexes} columns={indexCols} getRowId={(i) => i.name} />
        </TabsContent>

        <TabsContent value="statistics">
          <Card>
            <CardBody>
              <DataTable data={statistics} columns={statCols} getRowId={(s) => s.column} />
            </CardBody>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
